import React from 'react';
import { Header } from '../../common/header.component';
import { toMoney } from '../../../utils/common.utils';
import { AlertService } from '../../../services/alert.service';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { required, decimalValidation } from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { uploadMediaInputFiles } from '../../../services/file-service';
import { User, UserSelect } from '../../common/user-select';
import { MediaInput, MediaInputValue } from '../../common/media-input';
import get from 'lodash/get';
import { Select } from '../../common/select.component';
import { useSuppliers } from '../../../api/suppliers';
import { trpcSwr } from '../../../api/trpc';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

export const AdminCreateCustomsDeliveryOrderScreen: React.FC = () => {
  const navigate = useNavigate();
  const { trigger: createOrder } =
    trpcSwr.customsDelivery.createOrderByAdmin.useSWRMutation();

  async function handleSubmit(form: FormData) {
    const requestFilesResponse = await uploadMediaInputFiles(
      form.order.files || [],
    );

    await createOrder({
      clientId: form.user.id,
      supplierId: form.supplierId,
      name: form.order.name,
      price: +form.order.price,
      volume: +form.order.volume,
      weight: +form.order.weight,
      amount: +form.order.amount,
      comment: form.order.comment,
      files: requestFilesResponse.map((file) => ({
        id: file.file.id,
      })),
    });

    AlertService.success(
      <div>
        <div className="fw-medium">Заказ создан</div>
      </div>,
    );

    navigate(-1);
  }

  return (
    <>
      <Header />
      <div className="container" style={{ marginTop: -10 }}>
        <h1>Создать новый заказ на выкуп</h1>
        <PurchaseOrderForm onSubmit={handleSubmit} />
      </div>
    </>
  );
};

type FormData = {
  order: {
    name: string;
    price: string;
    requestCount?: string | null;
    weight: string;
    volume: string;
    amount: number;
    comment: string;
    files?: MediaInputValue[];
  };
  supplierId: number;
  user: User;
};

type Props = {
  onSubmit: (form: FormData) => Promise<void>;
};

const PurchaseOrderForm = (props: Props) => {
  const { data: suppliers } = useSuppliers();
  const intl = useIntl();

  const {
    register,
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    watch,
  } = useForm<FormData>({});

  const onSubmit = handleSubmit(async function (form) {
    await props.onSubmit(form);
  });

  const supplierId = watch('supplierId');

  const foundSupplier = suppliers?.find(
    (supplier) => supplier.id === supplierId,
  );
  const currencySupplier = foundSupplier?.purchaseCurrency || '';

  return (
    <form className="row justify-content-center pt-4" onSubmit={onSubmit}>
      <div className="col-7 p-4">
        <Controller
          name="supplierId"
          control={control}
          rules={required}
          render={({ field }) => (
            <Select
              className="mb-3"
              title="Поставщик"
              placeholder="Выберите поставщика"
              options={
                suppliers
                  ?.filter(({ location }) => location?.isCustomsDelivery)
                  .map(({ id, name, location }) => ({
                    value: id,
                    label:
                      name +
                      (location?.name ? ` (откуда: ${location.name})` : ''),
                  })) ?? []
              }
              {...field}
              error={errors.supplierId?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="user"
          rules={{
            ...required,
          }}
          render={({ field, fieldState }) => (
            <InputWrapper
              className="mb-3"
              title="Клиент"
              required
              error={fieldState.error?.message}
            >
              <UserSelect placeholder="Клиент" {...field} />
            </InputWrapper>
          )}
        />

        <div className="mb-4">
          <div className="card border-0 rounded">
            <div className="card-header bg-gray-50 border-0 p-3">
              <InputWrapper
                error={
                  // @ts-ignore todo
                  get(errors, props.path)?.name?.message
                }
              >
                <div className="col">
                  <input
                    type="text"
                    placeholder="Например, чехлы для телефонов"
                    className="form-control"
                    {...register('order.name')}
                  />
                </div>
              </InputWrapper>
            </div>

            <div className="card-body border border-gray-200 border-top-0 rounded-bottom">
              <Controller
                control={control}
                name={`order.files`}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    title={
                      <span>
                        Скриншоты, фотографии товара, инвойс или файл с заказом
                      </span>
                    }
                    className="mb-4"
                    error={fieldState.error?.message}
                  >
                    <MediaInput
                      {...field}
                      multiple
                      fileTypes={[
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'image/png',
                        'image/jpeg',
                        'video/*',
                        'application/pdf',
                      ]}
                    />
                  </InputWrapper>
                )}
              />

              <Controller
                control={control}
                name={`order.weight`}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'Укажите вес партии',
                    id: 'CustomsDeliveryItemForm.input-label.specifyWeight',
                    description: 'Пояснение к полю ввода',
                  }),
                }}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    required
                    title={intl.formatMessage({
                      defaultMessage: 'Вес общей партии',
                      id: 'CustomsDeliveryItemForm.input-label.weight',
                      description: 'Заголовок поля ввода',
                    })}
                    className="mb-4 text-base-lh-100"
                  >
                    <div className="relative mb-2 rounded d-flex gap-2">
                      <input
                        defaultValue={field.value}
                        onChange={field.onChange}
                        type="number"
                        step={0.01}
                        autoComplete="off"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'block w-full rounded border  text-left text-base-lh-125 text-gray-800 form-control',
                        )}
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                        <span className="text-base-lh-125 text-gray-400">
                          кг
                        </span>
                      </div>
                    </div>
                  </InputWrapper>
                )}
              />

              <Controller
                control={control}
                name={`order.volume`}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'Укажите объем партии',
                    id: 'CustomsDeliveryItemForm.input-label.specifyVolume',
                    description: 'Пояснение к полю ввода',
                  }),
                }}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    required
                    title={intl.formatMessage({
                      defaultMessage: 'Объем общей партии',
                      id: 'CustomsDeliveryItemForm.input-label.volume',
                      description: 'Заголовок поля ввода',
                    })}
                    className="mb-4 text-base-lh-100"
                  >
                    <div className="relative mb-2 rounded d-flex gap-2">
                      <input
                        defaultValue={field.value || undefined}
                        onChange={field.onChange}
                        type="number"
                        step={0.01}
                        autoComplete="off"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'block w-full rounded border text-left text-base-lh-125 text-gray-800 form-control',
                        )}
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                        <span className="text-base-lh-125 text-gray-400">
                          м3
                        </span>
                      </div>
                    </div>
                  </InputWrapper>
                )}
              />

              <Controller
                control={control}
                name={`order.amount`}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'Укажите количество штук',
                    id: 'CustomsDeliveryItemForm.input-label.specifyAmount',
                    description: 'Пояснение к полю ввода',
                  }),
                }}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    required
                    title={intl.formatMessage({
                      defaultMessage: 'Количество штук',
                      id: 'CustomsDeliveryItemForm.input-label.amount',
                      description: 'Заголовок поля ввода',
                    })}
                    className="mb-4 text-base-lh-100"
                  >
                    <div className="relative mb-2 rounded d-flex gap-2">
                      <input
                        defaultValue={field.value || undefined}
                        onChange={field.onChange}
                        type="number"
                        min={1}
                        step={1}
                        autoComplete="off"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'block w-full rounded border  text-left text-base-lh-125 text-gray-800 form-control',
                        )}
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                        <span className="text-base-lh-125 text-gray-400">
                          шт
                        </span>
                      </div>
                    </div>
                  </InputWrapper>
                )}
              />

              <Controller
                control={control}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'Укажите стоимость товаров',
                    id: 'CustomsDeliveryItemForm.input-label.specifyTheCostOfGoods',
                    description: 'Пояснение к полю ввода',
                  }),
                  validate: {
                    ...decimalValidation.validate,
                  },
                }}
                name={`order.price`}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    required
                    error={fieldState.error?.message}
                    title={intl.formatMessage({
                      defaultMessage: 'Общая стоимость товаров',
                      id: 'CustomsDeliveryItemForm.input-label.totalCostOfGoods',
                      description: 'Заголовок поля ввода',
                    })}
                    className="mb-4 text-base-lh-100"
                  >
                    <div className="relative rounded d-flex gap-2 h-auto">
                      <input
                        {...field}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'block w-full rounded border text-left text-base-lh-125 text-gray-800 form-control',
                        )}
                        aria-describedby="price-currency"
                        autoComplete="off"
                        placeholder="0"
                        data-testid="input-totalCost"
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                        <span className="text-base-lh-125 text-gray-400">
                          ¥
                        </span>
                      </div>
                    </div>
                  </InputWrapper>
                )}
              />

              <InputWrapper title="Комментарий">
                <textarea
                  style={{ height: '200px' }}
                  placeholder="Укажите здесь дополнительную информацию по товару"
                  className="form-control"
                  {...register('order.comment')}
                />
              </InputWrapper>
            </div>
          </div>
        </div>
      </div>
      <div className="col-5">
        <div className="bg-gray-50 p-4">
          <h3 className="mb-3">Общая стоимость заказа</h3>
          <div className="d-flex flex-row justify-content-between mb-3">
            <div className="fs-14 color-gray-400">Стоимость товаров</div>
            <div>{toMoney(0, `${currencySupplier}`)}</div>
          </div>
          <div className="d-flex flex-row justify-content-between mb-3">
            <div className="fs-14 color-gray-400">Комиссия за выкуп</div>
            <div>{toMoney(0, `${currencySupplier}`)}</div>
          </div>
          <div className="d-flex flex-row justify-content-between mb-3">
            <div className="fs-14 color-gray-400">
              Доставка товаров до транзитного склада
            </div>
            <div>
              <div>{toMoney(0, `${currencySupplier}`)}</div>
            </div>
          </div>
          <hr />
          <div className="d-flex flex-row justify-content-between mb-4">
            <div>Итого:</div>
            <div>
              <div>{toMoney(0, `${currencySupplier}`)}</div>
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-success w-100 mb-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Сохранить'
            )}
          </button>
        </div>
      </div>
    </form>
  );
};
